import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'

import { EMPTY, Observable } from 'rxjs'
import { catchError, map } from 'rxjs/operators'

import { environment as env } from '@env'
import { errorSnackBarConfig } from '@utils/snack-bar'

@Injectable({
	providedIn: 'root',
})
export class ChatService {
	readonly #http = inject(HttpClient)
	readonly #snackBar = inject(MatSnackBar)

	getDevices(): Observable<ChatDevice[]> {
		return this.#http.get<{ data: ChatDevice[] }>(`${env.endpoint}/notifications/chat/devices`).pipe(
			map(res => res.data),
			catchError((err: HttpErrorResponse) => this.#error(err))
		)
	}

	getConversations(requestPointUuid: string): Observable<ChatConversation[]> {
		return this.#http.get<{ data: ChatConversation[] }>(
			`${env.endpoint}/notifications/chat/conversations?requestPointUuid=${requestPointUuid}`
		).pipe(
			map(res => res.data),
			catchError((err: HttpErrorResponse) => this.#error(err))
		)
	}

	createConversation(payload: CreateConversationPayload): Observable<CreateConversationResponse> {
		return this.#http.post<{ data: CreateConversationResponse }>(
			`${env.endpoint}/notifications/chat/conversations`,
			payload
		).pipe(
			map(res => res.data),
			catchError((err: HttpErrorResponse) => this.#error(err))
		)
	}

	getMessages(conversationUuid: string): Observable<ChatMessage[]> {
		return this.#http.get<{ data: ChatMessage[] }>(
			`${env.endpoint}/notifications/chat/conversations/${conversationUuid}/messages`
		).pipe(
			map(res => res.data),
			catchError((err: HttpErrorResponse) => this.#error(err))
		)
	}

	sendMessage(payload: SendMessagePayload): Observable<SendMessageResponse> {
		return this.#http.post<{ data: SendMessageResponse }>(
			`${env.endpoint}/notifications/chat/messages`,
			payload
		).pipe(
			map(res => res.data),
			catchError((err: HttpErrorResponse) => this.#error(err))
		)
	}

	markAsRead(conversationUuid: string, requestPointUuid: string): Observable<void> {
		return this.#http.put<void>(
			`${env.endpoint}/notifications/chat/conversations/${conversationUuid}/read`,
			{ requestPointUuid }
		).pipe(
			catchError(() => EMPTY)
		)
	}

	#error(err: HttpErrorResponse): typeof EMPTY {
		const { panelClass, message, duration, action } = errorSnackBarConfig(err)
		this.#snackBar.open(message, action, { panelClass, duration })
		return EMPTY
	}
}
